/**
 * What a row in workflow_logs means.
 *
 * The Workflow History panel on a deal reads workflow_logs and splits it in
 * two: trigger_status 'failed' goes under "Failed Triggers (Needs Attention)",
 * trigger_status 'success' goes under "Completed Triggers". A row whose status
 * is neither word lands in neither list and is drawn nowhere at all.
 *
 * On 14 Sep 2026, 94 of the 280 rows on staging were in neither list:
 *
 *   'succeeded'  59  every resend, written through functions/api/_lib/repeatGuard
 *   'completed'  35  everything written through services/actionLog, plus two
 *                    inserts of its own in DealWorkspace
 *
 * So a bill of sale sent again, a buyer's details saved, a deal edited, an
 * inspection outcome recorded, a payment run started and a payout mismatch
 * resolved each wrote their audit row, and not one of them appeared on the
 * deal. A bounced email is the worst of them: smart-inbox-resend writes the
 * word 'bounced', which is a real failure that no screen shows and nobody is
 * asked to do anything about.
 *
 * Eight writers had four words for three ideas. This file holds the three, and
 * the reader goes through here so that no row can ever be invisible again -
 * a status nobody anticipated resolves off the boolean instead of vanishing.
 *
 * It is the same shape of defect as the status labels and the sold-status
 * lists, and it hides the same way: the screen looks fine. What is wrong with
 * it is a line that is not there, and nobody can report the absence of a line
 * they were never shown.
 */

/**
 * Three outcomes, because there are three.
 *
 * 'skipped' is the one the platform was missing. A trigger that was
 * deliberately not run is not a failure: a dealer, auction or tender seller
 * does not sign our Vehicle Purchase Agreement, so not texting them the link
 * is the system working. Filed as a failure it sat under "Needs Attention"
 * with a Retry button that could never succeed, on every fast-track deal,
 * for ever.
 */
export type WorkflowLogStatus = 'success' | 'failed' | 'skipped'

export const WORKFLOW_LOG_STATUSES: WorkflowLogStatus[] = ['success', 'failed', 'skipped']

/** What a person reads. The only labels for these three anywhere. */
export const WORKFLOW_LOG_STATUS_LABEL: Record<WorkflowLogStatus, string> = {
  success: 'Success',
  failed: 'Failed',
  skipped: 'Not applicable',
}

/**
 * The words already written to the table, and which of the three each one is.
 *
 * Kept rather than migrated away, because these rows are the audit trail and
 * rewriting history to fit a reader is the wrong way round. New writes use the
 * canonical word; old rows are understood on the way out.
 */
const SPELLINGS: Record<string, WorkflowLogStatus> = {
  success: 'success',
  succeeded: 'success',
  completed: 'success',
  complete: 'success',
  ok: 'success',
  failed: 'failed',
  failure: 'failed',
  error: 'failed',
  bounced: 'failed',
  complained: 'failed',
  rejected: 'failed',
  skipped: 'skipped',
  skip: 'skipped',
  not_applicable: 'skipped',
}

/**
 * Reasons a trigger deliberately did not run, as written into details before
 * there was a word for it.
 *
 * Rows already in the table say 'failed', because 'failed' was the only thing
 * the writer could say. They are not edited: an audit trail rewritten to suit
 * a reader is not an audit trail. They are read correctly on the way out
 * instead, off the reason the writer did record.
 */
const SKIP_REASONS = new Set(['seller_issues_own_invoice'])

export interface WorkflowLogLike {
  trigger_status?: string | null
  success?: boolean | null
  details?: Record<string, unknown> | null
}

/**
 * Which of the three this row is. Total: every row is exactly one of them.
 *
 * A word this file has never seen falls back to the boolean the same row
 * carries, so a writer added next year without reading this comment produces a
 * row that is still legible. Visible and roughly right beats invisible and
 * exactly right, because a person can see the first one and argue with it.
 */
export function workflowLogStatus(row: WorkflowLogLike | null | undefined): WorkflowLogStatus {
  const reason = row?.details && typeof row.details.reason === 'string' ? row.details.reason : ''
  if (SKIP_REASONS.has(reason)) return 'skipped'

  const raw = String(row?.trigger_status ?? '').trim().toLowerCase()
  const known = SPELLINGS[raw]
  if (known) return known
  return row?.success === true ? 'success' : 'failed'
}

/** Is this the kind of row a person has to do something about? */
export function needsAttention(row: WorkflowLogLike | null | undefined): boolean {
  return workflowLogStatus(row) === 'failed'
}

/** A step that did not apply to this deal. Nothing to retry, nothing to fix. */
export function wasSkipped(row: WorkflowLogLike | null | undefined): boolean {
  return workflowLogStatus(row) === 'skipped'
}

/**
 * The columns to write for an outcome, so every writer says the same thing.
 *
 * `error_message` carries the sentence for a skip as well as for a failure.
 * The column is named for the case it was built for; renaming it is a
 * migration and this is not the moment for one. The panel reads the status,
 * not the column name, and labels the box accordingly.
 */
export interface WorkflowLogWrite {
  trigger_status: WorkflowLogStatus
  success: boolean
  error_message: string | null
}

export function workflowLogFields(
  status: WorkflowLogStatus,
  message?: string | null,
): WorkflowLogWrite {
  return {
    trigger_status: status,
    success: status === 'success',
    error_message: status === 'success' ? null : message || null,
  }
}
